/**
 * localStorage-backed per-event preferences.
 *
 * Storage shape (key: `checkthelineup:prefs:v1`):
 *   {
 *     [eventSlug]: {
 *       artists: { [artistSlug]: "pass" | "meh" | "like" | "superlike" },
 *       sets?:    { [setId:   number]: "pass" | "meh" | "like" | "superlike" },
 *     }
 *   }
 *
 * Backwards-compat: older installs stored the artist map *directly* under
 * the event slug. `readAll` upgrades the shape in memory; `writeAll`
 * always writes the new nested form.
 *
 * Deliberately a pure module — easy to test, swap, or migrate to cloud later.
 */
export type Rating = "pass" | "meh" | "like" | "superlike";
export type PreferenceMap = Record<string, Rating>;
export type SetPreferenceMap = Record<number, Rating>;

export type EventPrefs = {
  artists: PreferenceMap;
  sets: SetPreferenceMap;
};

const KEY = "checkthelineup:prefs:v1";

type AllPrefs = Record<string, EventPrefs>;

/** Interpret any stored value under an event slug as the new EventPrefs shape. */
function normalize(raw: unknown): EventPrefs {
  if (!raw || typeof raw !== "object") return { artists: {}, sets: {} };
  const r = raw as Record<string, unknown>;
  // New shape
  if ("artists" in r || "sets" in r) {
    const artists = (r.artists && typeof r.artists === "object" ? r.artists : {}) as PreferenceMap;
    const sets = (r.sets && typeof r.sets === "object" ? r.sets : {}) as SetPreferenceMap;
    return { artists, sets };
  }
  // Legacy shape: the object itself is the artist→rating map.
  return { artists: r as PreferenceMap, sets: {} };
}

function readAll(): AllPrefs {
  if (typeof localStorage === "undefined") return {};
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};
    const out: AllPrefs = {};
    for (const [slug, v] of Object.entries(parsed)) out[slug] = normalize(v);
    return out;
  } catch {
    return {};
  }
}

function writeAll(all: AllPrefs): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(KEY, JSON.stringify(all));
  } catch {
    // Quota / disabled storage — fail silently; prefs just won't persist.
  }
}

/** @deprecated artist-only map; prefer {@link getEventPrefs}. Retained for
 *  broad call-site compatibility — many views only need the artist map. */
export function getPrefs(eventSlug: string): PreferenceMap {
  return readAll()[eventSlug]?.artists ?? {};
}

/** Full per-event preferences: artist ratings plus per-set overrides. */
export function getEventPrefs(eventSlug: string): EventPrefs {
  return readAll()[eventSlug] ?? { artists: {}, sets: {} };
}

export function getRating(eventSlug: string, artistSlug: string): Rating | null {
  return getEventPrefs(eventSlug).artists[artistSlug] ?? null;
}

export function getSetRating(eventSlug: string, setId: number): Rating | null {
  return getEventPrefs(eventSlug).sets[setId] ?? null;
}

/**
 * Effective rating for a specific set: a per-set override wins over the
 * artist-level rating; otherwise fall back to the artist rating.
 */
export function getEffectiveSetRating(
  eventSlug: string,
  artistSlug: string,
  setId: number,
): Rating | null {
  const ev = getEventPrefs(eventSlug);
  return ev.sets[setId] ?? ev.artists[artistSlug] ?? null;
}

export function setRating(
  eventSlug: string,
  artistSlug: string,
  rating: Rating | null,
): void {
  const all = readAll();
  const current = all[eventSlug] ?? { artists: {}, sets: {} };
  const artists = { ...current.artists };
  if (rating === null) delete artists[artistSlug];
  else artists[artistSlug] = rating;
  all[eventSlug] = { ...current, artists };
  writeAll(all);
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("prefs:changed", { detail: { eventSlug, artistSlug, rating } }));
  }
}

export function setSetRating(
  eventSlug: string,
  setId: number,
  rating: Rating | null,
): void {
  const all = readAll();
  const current = all[eventSlug] ?? { artists: {}, sets: {} };
  const sets = { ...current.sets };
  if (rating === null) delete sets[setId];
  else sets[setId] = rating;
  all[eventSlug] = { ...current, sets };
  writeAll(all);
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent("prefs:changed", { detail: { eventSlug, setId, rating, kind: "set" } }),
    );
  }
}

export function resetPrefs(eventSlug: string): number {
  const all = readAll();
  const current = all[eventSlug];
  const count = current
    ? Object.keys(current.artists).length + Object.keys(current.sets).length
    : 0;
  delete all[eventSlug];
  writeAll(all);
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("prefs:changed", { detail: { eventSlug, reset: true } }));
  }
  return count;
}

export type PrefStats = {
  total: number;
  pass: number;
  meh: number;
  like: number;
  superlike: number;
  rated: number;
};

export function getStats(eventSlug: string, artistCount: number): PrefStats {
  const prefs = getPrefs(eventSlug);
  const stats: PrefStats = { total: artistCount, pass: 0, meh: 0, like: 0, superlike: 0, rated: 0 };
  for (const r of Object.values(prefs)) {
    stats[r]++;
    stats.rated++;
  }
  return stats;
}
